import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { DEFAULT_CURRENCY } from 'components/FeaturedSpreadsheet/constants';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useDocumentFootnotes } from 'context/DocumentFootnotesContext';
import CitationService from 'services/citations';
import { useReviewDrawerOpen, useReviewerMode, useReviewItemStatus, useReviewStore } from 'store/useReviewStore';
import { AIFindingValueViewerProps } from './AIFindingValueViewer.props';
import { getAIFindingBoxSx } from './AIFindingValueViewer.styles';
import { restPurpleAnchorSx } from './AIMergeValueViewer.styles';
import AISecurityFindingPopover from './AISecurityFindingPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { formatDisplayValue } from '../../utilities/formatAiDisplayValue';
import { normalizeAiSelectValue, resolveAiOptionValue } from '../../utilities/resolveAiOptionValue';
import { reviewStatusOf } from '../../utilities/reviewAggregator';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';

const AIFindingValueViewer = ({
  value,
  aiFieldMetadata,
  aiAccuracyConfidence,
  fieldLabel,
  securityId,
  fieldAlias,
  onRemoveFinding,
  originalValueViewer: OriginalValueViewer,
  ...restProps
}: AIFindingValueViewerProps) => {
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const [, setLocalVersion] = useState(0);
  const { acceptField, dismissField } = useAIFieldAcceptContext();
  const { getDocumentMetadata } = useDocumentFootnotes();
  const citationService = useMemo(() => new CitationService(), []);

  // Same status derivation as AIMergeValueViewer: the review store item is
  // authoritative (it re-renders this viewer when the panel settles the card,
  // which a metadata mutation alone cannot do), metadata is the fallback.
  const storeStatus = useReviewItemStatus(securityId, fieldAlias);
  const status = storeStatus ?? reviewStatusOf(aiFieldMetadata);
  const isPending = status === 'pending';

  // SCAL-9150 parity with AIMergeValueViewer: this viewer wraps the NO-merge-diff
  // findings (suggestion equals the current value, or the cell is EMPTY - e.g.
  // the security-type / underlying-security dropdown rows), and previously
  // rendered NO pending indicator at all, so those cells never read purple in
  // review mode. While the drawer is open a pending finding paints purple; an
  // EMPTY cell additionally previews the FORMATTED AI suggestion - the gradient
  // is clipped to text, so an empty render would stay invisible.
  const drawerOpen = useReviewDrawerOpen();
  // SCAL-9335: the whole AI experience is analyst-side only; the early return
  // below renders the plain cell for everyone else.
  const reviewerMode = useReviewerMode();
  const restIsPurple = isPending && drawerOpen;
  const { format: spreadsheetFormat } = useContext(FeaturedSpreadsheetContext);
  const currencyCode = (spreadsheetFormat as { currency?: { code?: string } })?.currency?.code ?? DEFAULT_CURRENCY;
  const aiValue = aiFieldMetadata.value;
  const cellValue = restProps.cell?.value ?? value;
  const hasOwnValue = cellValue !== null && cellValue !== undefined && cellValue !== '';
  // Empty pending cell: preview the AI suggestion. Select-backed cells
  // (cell.options - e.g. Underlying Security) store option IDs while the AI
  // suggests the option's NAME, so resolve through the shared resolver and
  // preview the resolved option's LABEL; an unresolvable suggestion is still
  // shown but explicitly marked, so it can never read as a committed value
  // (accept refuses it - see resolveAcceptValue / CapTableWidgetDrawer).
  const aiPreview = useMemo(() => {
    if (!isPending || hasOwnValue || aiValue === undefined || aiValue === null) {
      return null;
    }
    const cell = restProps.cell as
      | { options?: { id?: unknown; name?: unknown }[]; dbType?: string; format?: RowFormat; gridType?: string }
      | undefined;
    if (Array.isArray(cell?.options)) {
      const resolved = resolveAiOptionValue(cell, aiValue);
      return resolved ? resolved.label : `${String(aiValue)} (no matching security)`;
    }
    // Yes/No + Cash-or-PIK rows are select cells WITHOUT cell.options: map the
    // suggestion into the option-index domain first so any AI spelling ('N',
    // 'FALSE', ' PIK ') previews as its option label through the shared formatter.
    const normalized = normalizeAiSelectValue(fieldAlias, aiValue);
    return formatDisplayValue(
      normalized ?? aiValue,
      cell?.dbType,
      fieldAlias,
      cell?.format,
      cell?.gridType,
      currencyCode
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, hasOwnValue, aiValue, restProps.cell, fieldAlias, currencyCode]);

  // Settle parity with the review panel: once the API call resolves, flip the
  // store item so the side-panel card moves to Reviewed instantly and (via
  // setReviewItemStatus) any stale hovered/selected ref on this cell clears.
  const handleAccept = useCallback(() => {
    if (securityId && fieldAlias) {
      acceptField(securityId, fieldAlias).then(() => {
        useReviewStore.getState().setReviewItemStatus(`${securityId}:${fieldAlias}`, 'accepted');
      });
    }
  }, [securityId, fieldAlias, acceptField]);

  const handleDismiss = useCallback(() => {
    if (securityId && fieldAlias) {
      dismissField(securityId, fieldAlias).then(() => {
        useReviewStore.getState().setReviewItemStatus(`${securityId}:${fieldAlias}`, 'dismissed');
      });
    }
  }, [securityId, fieldAlias, dismissField]);

  const pendingCitation = useMemo(
    () => restProps.cell?.citationReferences?.find((c: any) => c.is_accepted === false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restProps.cell?.citationReferences]
  );

  const pendingCitationDocumentName = useMemo(() => {
    if (!pendingCitation) {
      return undefined;
    }
    return getDocumentMetadata(pendingCitation.document_reference)?.documentName;
  }, [pendingCitation, getDocumentMetadata]);

  const handleAcceptCitation = useCallback(() => {
    if (!pendingCitation?.id || !restProps.cell?.citationReferences) {
      return;
    }

    const citationId = pendingCitation.id;

    // Update local cell state immediately for instant UI feedback
    // eslint-disable-next-line no-param-reassign
    restProps.cell.citationReferences = restProps.cell.citationReferences.map((c: any) =>
      c.id === citationId ? { ...c, is_accepted: true } : c
    );
    setLocalVersion(v => v + 1);

    citationService.updateCitationAcceptance(citationId, true).catch(() => {
      // Revert on failure
      // eslint-disable-next-line no-param-reassign
      restProps.cell.citationReferences = restProps.cell.citationReferences?.map((c: any) =>
        c.id === citationId ? { ...c, is_accepted: false } : c
      );
      setLocalVersion(v => v + 1);
    });
  }, [pendingCitation, restProps.cell, citationService]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        setCtrlHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        setCtrlHeld(false);
      }
    };
    const onBlur = () => setCtrlHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // SCAL-9335: non-reviewers get the plain cell - no AI popover, no purple
  // pending state, no AI-suggestion preview for empty cells.
  if (!reviewerMode) {
    return OriginalValueViewer ? (
      <OriginalValueViewer value={value} {...restProps} />
    ) : (
      <FastValueViewer value={value} {...restProps} />
    );
  }

  return (
    <AISecurityFindingPopover
      fieldMetadata={aiFieldMetadata}
      accuracyConfidence={aiAccuracyConfidence}
      fieldLabel={fieldLabel}
      status={status}
      onAccept={isPending && securityId && fieldAlias ? handleAccept : undefined}
      // eslint-disable-next-line no-nested-ternary
      onRemove={isPending ? (securityId && fieldAlias ? handleDismiss : onRemoveFinding) : undefined}
      pendingCitation={pendingCitation}
      onAcceptCitation={pendingCitation ? handleAcceptCitation : undefined}
      pendingCitationDocumentName={pendingCitationDocumentName}>
      {({ onClick }) => (
        <Box
          /*
          If Ctrl/Meta was held during the click, prevent default + stop propagation
          to the children and thus let the onClickCapture below open the AI popover.
          */
          onMouseDownCapture={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
            }
          }}
          onClickCapture={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
              onClick(e);
            }
          }}
          onContextMenuCapture={e => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
              onClick(e);
            }
          }}
          sx={getAIFindingBoxSx(ctrlHeld)}>
          {(() => {
            // Empty pending cell: preview the formatted AI suggestion (via a cell
            // clone - FastValueViewer reads cell.value). Otherwise the cell's own
            // viewer renders its real value, exactly as before.
            let content;
            if (aiPreview != null) {
              content = (
                <FastValueViewer
                  value={aiPreview}
                  {...restProps}
                  cell={{ ...restProps.cell, gridType: undefined, value: aiPreview }}
                />
              );
            } else if (OriginalValueViewer) {
              content = <OriginalValueViewer value={value} {...restProps} />;
            } else {
              content = <FastValueViewer value={value} {...restProps} />;
            }
            return restIsPurple ? (
              <Box component="span" sx={restPurpleAnchorSx} data-review-purple>
                {content}
              </Box>
            ) : (
              content
            );
          })()}
        </Box>
      )}
    </AISecurityFindingPopover>
  );
};

export default AIFindingValueViewer;
